import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import {
  formatAttendanceDay,
  formatDuration,
  formatTimeOfDay,
  getOrgDateString,
  isOpenRecord,
} from '@/modules/time-attendance/attendance/services/attendance-rules';
import {
  useGetAttendanceDays,
  useGetCorrectionStatusByDay,
} from '@/modules/time-attendance/attendance/hooks/use-attendance';
import {
  DAY_STATUS_LABEL_KEY,
  DayDisplayStatus,
} from '@/modules/time-attendance/attendance/components/day-status-badge/day-status-badge';
import {
  CORRECTION_STATUS_STYLE,
  CorrectionStatusTag,
} from '@/modules/time-attendance/attendance/components/correction-status-tag/correction-status-tag';
import {
  AttendanceDayRow,
  isCorrectableDay,
} from '@/modules/time-attendance/attendance/services/attendance-days';
import { AttendanceRecord } from '@/modules/time-attendance/attendance/types/attendance.types';

export interface AttendanceMonthlyGridProps {
  /** The employee whose month is shown. */
  userId: string;
  /** Offers a correction on each day that can be corrected, as the list does. */
  onRequestCorrection?: (row: AttendanceDayRow<AttendanceRecord>) => void;
}

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** The statuses counted in the legend (a day off is not). */
const STATUS_ORDER: DayDisplayStatus[] = [
  'WFH',
  'WFO',
  'Leave',
  'LeavePending',
  'Holiday',
  'Absent',
  'Working',
  'MissingClockOut',
];

const STATUS_STYLE: Partial<Record<DayDisplayStatus, string>> = {
  WFH: 'bg-success-subtle text-success',
  WFO: 'bg-accent text-primary',
  Leave: 'bg-warning-subtle text-warning',
  LeavePending: 'border border-dashed border-warning text-warning',
  Holiday: 'bg-warning-subtle text-warning',
  Weekend: 'bg-muted text-muted-foreground',
  Absent: 'bg-destructive-subtle text-destructive',
  Working: 'bg-accent text-primary',
  MissingClockOut: 'border border-destructive text-destructive',
};

const STATUS_DOT: Partial<Record<DayDisplayStatus, string>> = {
  WFH: 'bg-success',
  WFO: 'bg-primary',
  Leave: 'bg-warning',
  LeavePending: 'border border-warning',
  Holiday: 'bg-warning',
  Weekend: 'bg-muted-foreground',
  Absent: 'bg-destructive',
  Working: 'bg-primary',
  MissingClockOut: 'border border-destructive',
};

/** A time of the day's record, in the zone the record was taken in (its `Timezone`). */
const formatTime = (iso?: string, timeZone?: string) =>
  iso ? formatTimeOfDay(iso, false, timeZone) : null;
const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * Calendar-style month view of one employee's attendance — the equivalent of the legacy
 * `user-monthly-attendance-table`. Each day shows what the attendance list shows for it
 * (`buildAttendanceDays`): where it was worked, or a holiday, leave, a day off or a missed day.
 */
export const AttendanceMonthlyGrid = ({
  userId,
  onRequestCorrection,
}: AttendanceMonthlyGridProps) => {
  const { t } = useTranslation();

  // The organization's today and current month ("YYYY-MM"), never the device's.
  const today = getOrgDateString(new Date());
  const [month, setMonth] = useState<string>(() => today.slice(0, 7));

  // Built in UTC: the month is a calendar label, so no device zone or DST can shift its days.
  const [year, monthNumber] = month.split('-').map(Number);
  const monthIndex = monthNumber - 1;
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

  const monthStart = `${month}-01`;
  const monthEnd = `${month}-${pad2(daysInMonth)}`;
  const { data, isLoading } = useGetAttendanceDays(userId, monthStart, monthEnd);
  // Each day's correction (pending, else the latest), shown under the day once known.
  const { data: correctionStatusByDay, isLoading: isCorrectionStatusLoading } =
    useGetCorrectionStatusByDay(userId, monthStart, monthEnd);
  const daysByKey = useMemo(() => new Map((data ?? []).map((row) => [row.day, row])), [data]);

  const statusCounts = useMemo(() => {
    const counts = new Map<DayDisplayStatus, number>();
    for (const row of daysByKey.values()) {
      if (row.status) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
    }
    return counts;
  }, [daysByKey]);

  const firstWeekday = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
  const leading = Array.from({ length: firstWeekday }, () => null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const trailing = Array.from({ length: (7 - ((firstWeekday + daysInMonth) % 7)) % 7 }, () => null);
  const cells = [...leading, ...days, ...trailing];

  const goToMonth = (offset: number) => {
    const next = new Date(Date.UTC(year, monthIndex + offset, 1));
    setMonth(`${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}`);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => goToMonth(-1)}
              aria-label={t('PREVIOUS_MONTH')}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => goToMonth(1)}
              aria-label={t('NEXT_MONTH')}
            >
              <ChevronRight className="size-4" />
            </Button>
            <h2 className="ml-1 text-base font-semibold">
              {new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString(undefined, {
                timeZone: 'UTC',
                month: 'long',
                year: 'numeric',
              })}
            </h2>
            {/* Component Map › Attendance month calendar: Previous, Next and Today always. */}
            <Button
              variant="outline"
              size="sm"
              disabled={month === today.slice(0, 7)}
              onClick={() => setMonth(today.slice(0, 7))}
            >
              {t('TODAY')}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {STATUS_ORDER.map((status) => (
              <span
                key={status}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <span className={cn('size-2 rounded-full', STATUS_DOT[status])} />
                {t(DAY_STATUS_LABEL_KEY[status])}
                {/* Not "0" while the month loads: it would read as "none". */}
                {isLoading ? (
                  <Skeleton className="h-3 w-4" />
                ) : (
                  <span className="font-semibold text-foreground">
                    {statusCounts.get(status) ?? 0}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Prototype `.cal-grid7`: lg corners, a solid muted weekday row in normal case. */}
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-7 border-b border-border bg-muted">
            {WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className="px-2 py-2 text-center text-xs font-medium text-muted-foreground"
              >
                {t(label)}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {cells.map((day, index) => {
              const isLastColumn = index % 7 === 6;
              const isLastRow = index >= cells.length - 7;
              const cellBorder = cn({
                'border-r border-border': !isLastColumn,
                'border-b border-border': !isLastRow,
              });
              if (day === null) {
                return <div key={`blank-${index}`} className={cn('bg-surface-subtle', cellBorder)} />;
              }

              const dayKey = `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
              const dayRow = daysByKey.get(dayKey);
              const record = dayRow?.record ?? undefined;
              const status = dayRow?.status ?? null;
              const label = dayRow?.label || (status ? t(DAY_STATUS_LABEL_KEY[status]) : '');
              const start = formatTime(record?.StartTimeUtc, record?.Timezone);
              const end = isOpenRecord(record)
                ? null
                : formatTime(record?.EndTimeUtc, record?.Timezone);
              const worked = record?.TotalWorkTimeInSecond;
              const overtime = record?.OverTimeInSecond ?? 0;
              const isToday = dayKey === today;
              const correction = correctionStatusByDay?.[dayKey];
              // As the list: not until the corrections are known (the pencil might turn into a
              // pending tag), nor while one of the day is pending or approved (its tag shows instead).
              const canCorrect =
                !!onRequestCorrection &&
                !!dayRow &&
                !isCorrectionStatusLoading &&
                correction !== 'Pending' &&
                correction !== 'Approved' &&
                isCorrectableDay(dayRow);
              const correctLabel = `${t('REQUEST_CORRECTION')}: ${formatAttendanceDay(dayKey)}`;

              return (
                <div
                  key={day}
                  className={cn(
                    'group/day flex min-h-14 flex-col gap-1 p-1.5 sm:min-h-24 sm:p-2',
                    status === 'Weekend' && 'bg-surface-subtle',
                    cellBorder
                  )}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className={cn(
                        'flex size-6 items-center justify-center rounded-full text-xs font-medium',
                        isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                      )}
                    >
                      {day}
                    </span>
                    {/* On a phone the cell has room for dots only: the day's, then its correction's. */}
                    {(status || correction) && (
                      <span className="flex items-center gap-1 sm:hidden">
                        {status && (
                          <span
                            role="img"
                            className={cn('size-2 rounded-full', STATUS_DOT[status])}
                            aria-label={label}
                          />
                        )}
                        {correction && (
                          <span
                            role="img"
                            className={cn(
                              'size-2 rounded-full border',
                              CORRECTION_STATUS_STYLE[correction]
                            )}
                            aria-label={`${t('CORRECTION')}: ${t(correction.toUpperCase())}`}
                          />
                        )}
                      </span>
                    )}
                  </div>

                  {/* The month's days are loading: placeholders where the status and times go. */}
                  {isLoading && (
                    <div className="hidden flex-col gap-1.5 pt-1 sm:flex">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  )}

                  {/* A record saved before `Status` existed has no status but still its times. */}
                  {!isLoading && (status || record || correction) && (
                    <div className="hidden flex-col gap-1 sm:flex">
                      {status && (
                        <span
                          className={cn(
                            'w-fit rounded px-1.5 py-0.5 text-[11px] font-medium',
                            STATUS_STYLE[status]
                          )}
                        >
                          {label}
                        </span>
                      )}
                      {start && (
                        <span className="text-xs text-muted-foreground">
                          {start}
                          {end ? ` – ${end}` : ''}
                        </span>
                      )}
                      {status !== 'Working' &&
                        status !== 'MissingClockOut' &&
                        worked != null &&
                        worked > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {formatDuration(worked)}
                            {overtime > 0 && (
                              <span className="ml-1 font-medium text-success">
                                +{formatDuration(overtime)}
                              </span>
                            )}
                          </span>
                        )}
                      {correction && (
                        <CorrectionStatusTag
                          status={correction}
                          withIcon
                          className="rounded px-1.5 py-0 text-[11px]"
                        />
                      )}
                    </div>
                  )}

                  {canCorrect && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      // On a pointer screen it shows on hover or keyboard focus (Component Map ›
                      // correctable days get a pencil on hover); phones have no hover, so it stays.
                      className="mt-auto size-6 self-end sm:size-8 sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover/day:opacity-100"
                      // The day in the name: a month of these buttons is otherwise indistinguishable.
                      aria-label={correctLabel}
                      title={correctLabel}
                      onClick={() => onRequestCorrection(dayRow)}
                    >
                      <Pencil className="size-4 sm:size-5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
